import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const Tab = ({ active, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`flex-1 rounded-lg py-2 text-sm font-semibold ${active ? "bg-white text-[#1e3a8a] shadow-sm" : "text-slate-500 hover:text-[#0f172a]"}`}
    style={{ transition: "background-color 160ms, color 160ms" }}>
    {children}
  </button>
);

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4"><path fill="#4285F4" d="M22.6 12.2c0-.8-.1-1.5-.2-2.2H12v4.2h5.9a5 5 0 0 1-2.2 3.3v2.7h3.6c2-1.9 3.3-4.7 3.3-8z"/><path fill="#34A853" d="M12 23c3 0 5.5-1 7.3-2.7l-3.6-2.7c-1 .7-2.2 1.1-3.7 1.1-2.9 0-5.3-1.9-6.2-4.5H2.1v2.8A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.8 14.2a6.6 6.6 0 0 1 0-4.3V7.1H2.1a11 11 0 0 0 0 9.9l3.7-2.8z"/><path fill="#EA4335" d="M12 5.4c1.6 0 3.1.6 4.2 1.7l3.2-3.2A11 11 0 0 0 2.1 7.1l3.7 2.8C6.7 7.3 9.1 5.4 12 5.4z"/></svg>
);

export default function AuthPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/app" replace />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(`/auth/${mode === "login" ? "login" : "register"}`, payload);
      setUser(data);
      navigate("/app", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <aside className="relative hidden flex-col justify-between overflow-hidden bg-[#172554] p-12 text-white grain lg:flex">
        <Logo light />
        <div className="relative">
          <h2 className="text-4xl font-bold leading-tight tracking-tight">Locazioni trasparenti,<br />decisioni informate.</h2>
          <ul className="mt-8 space-y-3 text-slate-300">
            {["Monitoraggio canoni a semaforo", "Ricerca nelle convalide di sfratto verificate", "Passaporto dell'inquilino condivisibile"].map((t) => (
              <li key={t} className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-[#93c5fd]" />{t}</li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-slate-400">Conforme al Regolamento UE 2016/679 (GDPR)</p>
      </aside>
      <main className="flex items-center justify-center bg-[#f8fafc] px-4 py-12">
        <div className="w-full max-w-md fade-up">
          <div className="mb-10 lg:hidden"><Logo /></div>
          <h1 className="text-3xl font-bold tracking-tight text-[#0f172a]">{mode === "login" ? "Bentornato" : "Crea il tuo account"}</h1>
          <p className="mt-2 text-sm text-[#64748b]">Accedi alla WebApp AffittoSicuro per gestire i tuoi immobili.</p>
          <div className="mt-8 flex gap-1 rounded-xl bg-slate-100 p-1">
            <Tab active={mode === "login"} onClick={() => { setMode("login"); setError(""); }} testid="auth-modal-login-tab">Accedi</Tab>
            <Tab active={mode === "register"} onClick={() => { setMode("register"); setError(""); }} testid="auth-modal-register-tab">Registrati</Tab>
          </div>
          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <div><label className="field-label">Nome e cognome</label><input data-testid="auth-input-name" required className="field-input" value={form.name} onChange={set("name")} placeholder="Mario Rossi" /></div>
            )}
            <div><label className="field-label">Email</label><input data-testid="auth-input-email" type="email" required className="field-input" value={form.email} onChange={set("email")} placeholder="nome@esempio.it" /></div>
            <div><label className="field-label">Password</label><input data-testid="auth-input-password" type="password" required minLength={mode === "register" ? 8 : undefined} className="field-input" value={form.password} onChange={set("password")} placeholder={mode === "register" ? "Minimo 8 caratteri" : "••••••••"} /></div>
            {error && <p data-testid="auth-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-[#dc2626]">{error}</p>}
            <button data-testid="auth-button-submit" type="submit" disabled={loading} className="btn-accent w-full">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}{mode === "login" ? "Accedi" : "Crea account"}
            </button>
          </form>
          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wider text-slate-400"><span className="h-px flex-1 bg-slate-200" />oppure<span className="h-px flex-1 bg-slate-200" /></div>
          <button data-testid="auth-button-google" type="button" onClick={googleLogin} className="btn-ghost w-full py-3"><GoogleIcon />Continua con Google</button>
        </div>
      </main>
    </div>
  );
}
